import { useReducer, useCallback } from 'react';
import { FormState, FormAction, FormDefinition, FormDraft, FormResponses } from '../types/form.types';

const initialState: FormState = {
    formId: '',
    formDefinition: null,
    currentStepIndex: 0,
    responses: {},
    errors: {},
    uploadedFiles: [],
    isSubmitting: false,
    isLoading: false,
    isDraftSaved: false,
    lastSavedAt: null
};

function formReducer(state: FormState, action: FormAction): FormState {
    switch (action.type) {
        case 'SET_FORM_DEFINITION':
            return {
                ...state,
                formDefinition: action.payload,
                formId: action.payload.formId,
                isLoading: false
            };

        case 'SET_CURRENT_STEP':
            return {
                ...state,
                currentStepIndex: action.payload
            };

        case 'UPDATE_RESPONSES':
            // Filter out undefined values from payload
            const filteredPayload = Object.entries(action.payload).reduce((acc, [key, value]) => {
                if (value !== undefined) {
                    acc[key] = value;
                }
                return acc;
            }, {} as FormResponses);

            return {
                ...state,
                responses: {
                    ...state.responses,
                    ...filteredPayload
                },
                isDraftSaved: false
            };

        case 'SET_ERRORS':
            return {
                ...state,
                errors: action.payload
            };

        case 'CLEAR_ERROR':
            const { [action.payload]: _, ...remainingErrors } = state.errors;
            return {
                ...state,
                errors: remainingErrors
            };

        case 'ADD_UPLOADED_FILE':
            return {
                ...state,
                uploadedFiles: [...state.uploadedFiles, action.payload]
            };

        case 'SET_SUBMITTING':
            return {
                ...state,
                isSubmitting: action.payload
            };

        case 'SET_LOADING':
            return {
                ...state,
                isLoading: action.payload
            };

        case 'SET_DRAFT_SAVED':
            return {
                ...state,
                isDraftSaved: action.payload,
                lastSavedAt: action.payload ? new Date() : state.lastSavedAt
            };

        case 'RESTORE_DRAFT':
            const stepIndex = state.formDefinition?.steps.findIndex(
                (step) => step.stepId === action.payload.currentStep
            ) || 0;

            return {
                ...state,
                currentStepIndex: stepIndex,
                responses: action.payload.responses,
                uploadedFiles: action.payload.uploadedFiles,
                isDraftSaved: true,
                lastSavedAt: new Date(action.payload.startedAt)
            };

        case 'RESET_FORM':
            return {
                ...initialState,
                formId: state.formId,
                formDefinition: state.formDefinition
            };

        default:
            return state;
    }
}

export const useFormState = (formId: string) => {
    const [state, dispatch] = useReducer(formReducer, {
        ...initialState,
        formId
    });

    const setFormDefinition = useCallback((definition: FormDefinition) => {
        dispatch({ type: 'SET_FORM_DEFINITION', payload: definition });
    }, []);

    const setCurrentStep = useCallback((stepIndex: number) => {
        dispatch({ type: 'SET_CURRENT_STEP', payload: stepIndex });
    }, []);

    const updateResponses = useCallback((responses: Partial<FormResponses>) => {
        dispatch({ type: 'UPDATE_RESPONSES', payload: responses });
    }, []);

    const updateSingleResponse = useCallback((fieldId: string, value: string | number | string[]) => {
        dispatch({ type: 'UPDATE_RESPONSES', payload: { [fieldId]: value } });
    }, []);

    const setErrors = useCallback((errors: { [fieldId: string]: string }) => {
        dispatch({ type: 'SET_ERRORS', payload: errors });
    }, []);

    const clearError = useCallback((fieldId: string) => {
        dispatch({ type: 'CLEAR_ERROR', payload: fieldId });
    }, []);

    const addUploadedFile = useCallback((fileId: string) => {
        dispatch({ type: 'ADD_UPLOADED_FILE', payload: fileId });
    }, []);

    const setSubmitting = useCallback((isSubmitting: boolean) => {
        dispatch({ type: 'SET_SUBMITTING', payload: isSubmitting });
    }, []);

    const setLoading = useCallback((isLoading: boolean) => {
        dispatch({ type: 'SET_LOADING', payload: isLoading });
    }, []);

    const setDraftSaved = useCallback((isSaved: boolean) => {
        dispatch({ type: 'SET_DRAFT_SAVED', payload: isSaved });
    }, []);

    const restoreDraft = useCallback((draft: FormDraft) => {
        dispatch({ type: 'RESTORE_DRAFT', payload: draft });
    }, []);

    const resetForm = useCallback(() => {
        dispatch({ type: 'RESET_FORM' });
    }, []);

    const nextStep = useCallback(() => {
        if (state.formDefinition && state.currentStepIndex < state.formDefinition.steps.length - 1) {
            setCurrentStep(state.currentStepIndex + 1);
        }
    }, [state.formDefinition, state.currentStepIndex, setCurrentStep]);

    const previousStep = useCallback(() => {
        if (state.currentStepIndex > 0) {
            setCurrentStep(state.currentStepIndex - 1);
        }
    }, [state.currentStepIndex, setCurrentStep]);

    const getCurrentStep = useCallback(() => {
        return state.formDefinition?.steps[state.currentStepIndex] || null;
    }, [state.formDefinition, state.currentStepIndex]);

    const isFirstStep = state.currentStepIndex === 0;
    const isLastStep = state.formDefinition
        ? state.currentStepIndex === state.formDefinition.steps.length - 1
        : false;

    return {
        state,
        setFormDefinition,
        setCurrentStep,
        updateResponses,
        updateSingleResponse,
        setErrors,
        clearError,
        addUploadedFile,
        setSubmitting,
        setLoading,
        setDraftSaved,
        restoreDraft,
        resetForm,
        nextStep,
        previousStep,
        getCurrentStep,
        isFirstStep,
        isLastStep
    };
};
